import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function ScheduledShows({ onDataLoaded }) {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchScheduled = async () => {
      try {
        const headers = {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123'
        };
        const res = await axios.get('/api/fetch_scheduled_shows', { headers });
        if (res.data) {
          setShows(res.data);
          if (onDataLoaded) onDataLoaded(res.data.length > 0);
        } else {
          if (onDataLoaded) onDataLoaded(false);
        }
      } catch (error) {
        console.error('Error fetching scheduled shows:', error);
        if (onDataLoaded) onDataLoaded(false);
      } finally {
        setLoading(false);
      }
    };
    fetchScheduled();
  }, []);

  if (shows.length === 0 && !loading) return null;

  const formatTime = (timeStr) => {
    if (!timeStr) return '11:00 PM';
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) return timeStr;
    const [h, m] = timeStr.split(':');
    if (h && m) {
      const hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${m} ${ampm}`;
    }
    return timeStr;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-base md:text-xl font-bold text-gray-900 border-l-4 border-red-600 pl-3">Scheduled Show List</h2>
        
        {/* Desktop View All Pill */}
        <button 
          onClick={() => navigate('/shows')}
          className="hidden sm:flex text-gray-500 hover:text-red-600 text-[11px] font-bold border border-gray-200 px-3 py-1 rounded-full shadow-xs hover:bg-gray-50 transition-colors"
        >
          View All &rarr;
        </button>

        {/* Mobile Arrow Button Only */}
        <button 
          onClick={() => navigate('/shows')}
          className="flex sm:hidden w-7 h-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-red-600 hover:border-red-300 transition-colors shadow-xs shrink-0"
          aria-label="View All Scheduled Shows"
        >
          <ArrowRight size={14} />
        </button>
      </div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="space-y-4"
      >
         {shows.slice(0, 5).map((show) => {
           const dateStr = show.jatra_date ? new Date(show.jatra_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'Scheduled';
           const timeStr = formatTime(show.jatra_time || show.time);

           return (
             <motion.div variants={itemVariants} key={show.product_id} className="flex items-center gap-3">
                {/* Thumbnail */}
                <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 border border-gray-100 bg-white p-1">
                   <img src={show.image ? show.image : ''} alt={show.jatra_name} className="w-full h-full object-cover rounded-full" onError={(e) => { e.target.onerror = null; e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"; }} />
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                   <h3 className="text-sm font-bold text-gray-900 truncate">{show.jatra_name}</h3>
                   <p className="text-xs text-red-500 font-semibold truncate">{show.cat_name}</p>
                </div>

                {/* Date & Time Badge */}
                <div className="text-right shrink-0">
                   <span className="text-[11px] font-bold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-md flex items-center gap-1">
                     <span>{dateStr}</span>
                     <span className="text-gray-400">•</span>
                     <span className="text-red-600 font-medium">{timeStr}</span>
                   </span>
                </div>
             </motion.div>
           );
         })}
      </motion.div>
    </div>
  );
}
